import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsVector2 {
  public readonly values: HsHypersceneTypes.Array2;

  constructor(x: number, y: number) {
    this.values = [x, y];
  }

  /**
   * Handles x.
   * @returns Result value.
   */
  public get x(): number {
    return this.values[0];
  }

  /**
   * Handles y.
   * @returns Result value.
   */
  public get y(): number {
    return this.values[1];
  }

  /**
   * Sets set.
   * @param x x.
   * @param y y.
   * @returns Result value.
   */
  public set(x: number, y: number): HsVector2 {
    this.values[0] = x;
    this.values[1] = y;
    return this;
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array2): HsVector2 {
    this.values[0] = array[0];
    this.values[1] = array[1];
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsVector2): HsVector2 {
    return this.set(other.x, other.y);
  }

  /**
   * Adds add.
   * @param other other.
   * @returns Result value.
   */
  public add(other: HsVector2): HsVector2 {
    return this.set(this.x + other.x, this.y + other.y);
  }

  /**
   * Handles subtract.
   * @param other other.
   * @returns Result value.
   */
  public subtract(other: HsVector2): HsVector2 {
    return this.set(this.x - other.x, this.y - other.y);
  }

  /**
   * Adds vectors.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public addVectors(a: HsVector2, b: HsVector2): HsVector2 {
    return this.set(a.x + b.x, a.y + b.y);
  }

  /**
   * Handles subtract vectors.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public subtractVectors(a: HsVector2, b: HsVector2): HsVector2 {
    return this.set(a.x - b.x, a.y - b.y);
  }

  /**
   * Handles multiply by scalar.
   * @param multiplier multiplier.
   * @returns Result value.
   */
  public multiplyByScalar(multiplier: number): HsVector2 {
    return this.set(this.x * multiplier, this.y * multiplier);
  }

  /**
   * Handles divide by scalar.
   * @param devisor devisor.
   * @returns Result value.
   */
  public divideByScalar(devisor: number): HsVector2 {
    return this.set(this.x / devisor, this.y / devisor);
  }

  /**
   * Handles normalize.
   * @returns Result value.
   */
  public normalize(): HsVector2 {
    const length: number = Math.sqrt(this.x * this.x + this.y * this.y);
    return this.set(this.x / length, this.y / length);
  }

  /**
   * Handles negate.
   * @returns Result value.
   */
  public negate(): HsVector2 {
    return this.set(-this.x, -this.y);
  }

  /**
   * Sets from lerp.
   * @param vector1 vector1.
   * @param vector2 vector2.
   * @param ratio ratio.
   * @returns Result value.
   */
  public setFromLerp(vector1: HsVector2, vector2: HsVector2, ratio: number): HsVector2 {
    const x: number = vector1.x * (1 - ratio) + vector2.x * ratio;
    const y: number = vector1.y * (1 - ratio) + vector2.y * ratio;
    return this.set(x, y);
  }

  /**
   * Handles min.
   * @param other other.
   * @returns Result value.
   */
  public min(other: HsVector2): HsVector2 {
    const x: number = Math.min(this.x, other.x);
    const y: number = Math.min(this.y, other.y);
    return this.set(x, y);
  }

  /**
   * Handles max.
   * @param other other.
   * @returns Result value.
   */
  public max(other: HsVector2): HsVector2 {
    const x: number = Math.max(this.x, other.x);
    const y: number = Math.max(this.y, other.y);
    return this.set(x, y);
  }

  /**
   * Handles clamp.
   * @param minVector min vector.
   * @param maxVector max vector.
   * @returns Result value.
   */
  public clamp(minVector: HsVector2, maxVector: HsVector2): HsVector2 {
    const x: number = Math.min(maxVector.x, Math.max(this.x, minVector.x));
    const y: number = Math.min(maxVector.y, Math.max(this.y, minVector.y));
    return this.set(x, y);
  }

  /**
   * Handles length squared.
   * @returns Result value.
   */
  public lengthSquared(): number {
    return this.x * this.x + this.y * this.y;
  }

  /**
   * Handles length.
   * @returns Result value.
   */
  public length(): number {
    return Math.sqrt(this.lengthSquared());
  }

  /**
   * Builds zero vector.
   * @returns Result value.
   */
  public static makeZeroVector(): HsVector2 {
    return new HsVector2(0, 0);
  }

  /**
   * Builds unit vector x.
   * @returns Result value.
   */
  public static makeUnitVectorX(): HsVector2 {
    return new HsVector2(1, 0);
  }

  /**
   * Builds unit vector y.
   * @returns Result value.
   */
  public static makeUnitVectorY(): HsVector2 {
    return new HsVector2(0, 1);
  }

  /**
   * Adds add.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static add(a: HsVector2, b: HsVector2): HsVector2 {
    return new HsVector2(a.x + b.x, a.y + b.y);
  }

  /**
   * Handles subtract.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static subtract(a: HsVector2, b: HsVector2): HsVector2 {
    return new HsVector2(a.x - b.x, a.y - b.y);
  }

  /**
   * Handles lerp.
   * @param a a.
   * @param b b.
   * @param ratio ratio.
   * @returns Result value.
   */
  public static lerp(a: HsVector2, b: HsVector2, ratio: number): HsVector2 {
    const x: number = a.x * (1 - ratio) + b.x * ratio;
    const y: number = a.y * (1 - ratio) + b.y * ratio;
    return new HsVector2(x, y);
  }

  /**
   * Handles dot.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static dot(a: HsVector2, b: HsVector2): number {
    return a.x * b.x + a.y * b.y;
  }

  /**
   * Handles distance squared.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static distanceSquared(a: HsVector2, b: HsVector2): number {
    const dx: number = a.x - b.x;
    const dy: number = a.y - b.y;
    return dx * dx + dy * dy;
  }

  /**
   * Handles distance.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static distance(a: HsVector2, b: HsVector2): number {
    return Math.sqrt(HsVector2.distanceSquared(a, b));
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsVector2): HsVector2 {
    return new HsVector2(source.x, source.y);
  }
}
